// @ts-check
// Página de painéis (contrato 07, seção 6): /dashboards abre o primeiro; cabeçalho com o seletor (lista, novo,
// duplicar, renomear, excluir), barra de filtros com estado na URL, abas (aba aberta em ?tab=), grade de widgets da aba e
// modo de edição com "Adicionar widget", abas, "Cancelar" e "Salvar".
import { html } from 'htm/preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { navigate, searchParamsState, updateSearchParams } from '../../app/router.js';
import { currentWorkspaceMemberState, hasCurrentPermissionFlag, sessionState } from '../../data/metadata-store.js';
import { createDashboard, deleteDashboard, updateDashboard } from '../../data/dashboards.js';
import { getErrorMessage } from '../../data/api-client.js';
import { hasStageScope } from '../../data/stage-scope.js';
import { readFilterState, toDefaultFilters, toRequestFilters, toSearchParamChanges } from '../../lib/dashboard-filters.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { Icon } from '../../ui/icon.js';
import { Button, IconButton } from '../../ui/button.js';
import { Dropdown, DropdownMenuItems, DropdownMenuSeparator, MenuItem } from '../../ui/dropdown.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { Modal } from '../../ui/modal.js';
import { useIsMobile } from '../../ui/hooks.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { closeSidePanel, openDashboardWidgetPanel, sidePanelState } from '../side-panel/side-panel-state.js';
import { DashboardFilterBar } from './dashboard-filter-bar.js';
import { DashboardGrid } from './dashboard-grid.js';
import { DashboardTabBar } from './dashboard-tab-bar.js';
import { widgetsInTab } from '../../lib/dashboard-tabs.js';
import { DASHBOARD_WIDGET_PARAM } from '../../lib/drilldown-links.js';
import {
  activeTabDraftWidgetsState,
  activeTabKeyState,
  addDraftTab,
  currentDashboardState,
  dashboardsState,
  duplicateDraftTab,
  duplicateDraftWidget,
  moveDraftTab,
  moveDraftWidgetToTab,
  removeDraftTab,
  requestedTabKeyState,
  selectTab,
  tabsState,
  updateDraftTab,
  isDraftDirtyState,
  isEditingState,
  openDashboard,
  refreshDashboards,
  removeDraftWidget,
  requestFiltersState,
  saveDraft,
  selectedWidgetKeyState,
  setDraftLayout,
  startEditing,
  stopEditing,
} from './dashboard-state.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {import('../../data/dashboards.js').Dashboard} Dashboard
 * @typedef {import('../../lib/dashboard-filters.js').DashboardFilterState} DashboardFilterState
 * @typedef {{ kind: 'create' } | { kind: 'rename' } | { kind: 'duplicate' } | { kind: 'delete' } | null} DialogState
 */

const FILTER_PARAM_KEYS = ['period', 'from', 'to', 'campusId', 'ownerWorkspaceMemberId', 'academicPeriodId'];

/**
 * Caminho do painel aberto no lugar do pedido: mantém os filtros e tira `tab` quando não é aba do painel aberto.
 * @param {Dashboard} dashboard @param {string} search
 */
export function redirectedDashboardPath(dashboard, search) {
  const parameters = new URLSearchParams(search);
  const tab = parameters.get('tab');
  if (tab && !(dashboard.tabs ?? []).some((candidate) => candidate.id === tab)) parameters.delete('tab');
  const query = parameters.toString();
  return `/dashboards/${dashboard.id}${query ? `?${query}` : ''}`;
}

/**
 * @param {Dashboard | null} dashboard
 */
function canEditDashboard(dashboard) {
  if (!dashboard) return false;
  // sem DASHBOARDS (Coordenação: só o Relatório de visitas) os painéis são só leitura
  if (!hasCurrentPermissionFlag('DASHBOARDS')) return false;
  if (typeof dashboard.canEdit === 'boolean') return dashboard.canEdit;
  if (sessionState.value?.role === 'ADMIN') return true;
  const memberId = currentWorkspaceMemberState.value?.id;
  return Boolean(memberId && dashboard.createdByWorkspaceMemberId === memberId);
}

/**
 * @param {{ dashboard: Dashboard | null, onAction: (kind: 'create' | 'rename' | 'duplicate' | 'delete') => void, canEdit: boolean, disabled: boolean }} props
 */
function DashboardPicker({ dashboard, onAction, canEdit, disabled }) {
  const anchorRef = useRef(/** @type {HTMLButtonElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const dashboards = dashboardsState.value ?? [];
  const run = (/** @type {'create' | 'rename' | 'duplicate' | 'delete'} */ kind) => {
    setIsOpen(false);
    onAction(kind);
  };
  return html`<div class="dashboard-picker">
    <button ref=${anchorRef} type="button" class="view-picker-button dashboard-picker-button" aria-haspopup="listbox" aria-expanded=${isOpen ? 'true' : 'false'}
      disabled=${disabled} onClick=${() => setIsOpen(!isOpen)}>
      <${Icon} name=${dashboard?.icon ?? 'IconLayoutDashboard'} size=${16} />
      <span class="dashboard-picker-name">${dashboard?.name ?? t('dashboards.title')}</span>
      <${Icon} name="IconChevronDown" size=${14} className="view-picker-chevron" />
    </button>
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} width=${260}>
      <${DropdownMenuItems} hasMaxHeight=${true}>
        ${dashboards.map((candidate) => html`<${MenuItem}
          key=${candidate.id}
          icon=${candidate.icon ?? 'IconLayoutDashboard'}
          text=${candidate.name}
          contextualText=${candidate.widgetCount !== undefined ? t('dashboards.widgetCount', { count: candidate.widgetCount }) : undefined}
          selected=${candidate.id === dashboard?.id}
          onClick=${() => {
            setIsOpen(false);
            if (candidate.id !== dashboard?.id) navigate(`/dashboards/${candidate.id}`);
          }}
        />`)}
      <//>
      ${hasCurrentPermissionFlag('DASHBOARDS')
        ? html`<${DropdownMenuSeparator} />
          <${DropdownMenuItems}>
            <${MenuItem} icon="IconPlus" text=${t('dashboards.actions.create')} onClick=${() => run('create')} />
            ${dashboard ? html`<${MenuItem} icon="IconCopy" text=${t('dashboards.actions.duplicate')} onClick=${() => run('duplicate')} />` : null}
            ${dashboard && canEdit ? html`<${MenuItem} icon="IconPencil" text=${t('dashboards.actions.rename')} onClick=${() => run('rename')} />` : null}
            ${dashboard && canEdit ? html`<${MenuItem} icon="IconTrash" accent="danger" text=${t('dashboards.actions.delete')} onClick=${() => run('delete')} />` : null}
          <//>`
        : null}
    <//>
  </div>`;
}

/**
 * @param {{ dialog: NonNullable<DialogState>, dashboard: Dashboard | null, onClose: () => void, onDone: (dashboardId: string | null) => void }} props
 */
function DashboardDialog({ dialog, dashboard, onClose, onDone }) {
  const initialName = dialog.kind === 'rename'
    ? dashboard?.name ?? ''
    : dialog.kind === 'duplicate'
      ? t('dashboards.copyName', { name: dashboard?.name ?? '' })
      : '';
  const [name, setName] = useState(initialName);
  const [isLoading, setIsLoading] = useState(false);
  const confirm = async () => {
    setIsLoading(true);
    try {
      if (dialog.kind === 'delete' && dashboard) {
        await deleteDashboard(dashboard.id);
        showSuccessSnackbar(t('dashboards.messages.deleted', { name: dashboard.name }));
        onDone(null);
      } else if (dialog.kind === 'rename' && dashboard) {
        await updateDashboard(dashboard.id, { name: name.trim() });
        currentDashboardState.value = { ...dashboard, name: name.trim() };
        onDone(dashboard.id);
      } else {
        const created = await createDashboard({
          name: name.trim(),
          icon: dialog.kind === 'duplicate' ? dashboard?.icon ?? undefined : 'IconLayoutDashboard',
          ...(dialog.kind === 'duplicate' && dashboard ? { duplicateFromId: dashboard.id } : {}),
        });
        showSuccessSnackbar(t(dialog.kind === 'duplicate' ? 'dashboards.messages.duplicated' : 'dashboards.messages.created', { name: name.trim() }));
        onDone(created?.id ?? null);
      }
    } catch (error) {
      setIsLoading(false);
      showErrorSnackbar(error);
    }
  };
  if (dialog.kind === 'delete') {
    return html`<${Modal}
      title=${t('dashboards.dialogs.deleteTitle')}
      subtitle=${t('dashboards.dialogs.deleteSubtitle', { name: dashboard?.name ?? '' })}
      confirmLabel=${t('dashboards.actions.delete')}
      confirmAccent="danger"
      isLoading=${isLoading}
      onClose=${onClose}
      onConfirm=${confirm}
    />`;
  }
  return html`<${Modal}
    title=${t(`dashboards.dialogs.${dialog.kind}Title`)}
    confirmLabel=${t(dialog.kind === 'rename' ? 'common.save' : 'dashboards.dialogs.createConfirm')}
    isConfirmDisabled=${!name.trim()}
    isLoading=${isLoading}
    onClose=${onClose}
    onConfirm=${confirm}
  >
    <label class="form-field">
      <span class="form-field-label">${t('dashboards.dialogs.name')}</span>
      <input class="text-input" value=${name} maxLength=${120} placeholder=${t('dashboards.dialogs.namePlaceholder')}
        ref=${(element) => {
          if (element && !element.dataset.focused) {
            element.dataset.focused = 'true';
            requestAnimationFrame(() => element.select());
          }
        }}
        onInput=${(event) => setName(/** @type {HTMLInputElement} */ (event.currentTarget).value)} />
    </label>
  <//>`;
}

function DashboardSkeleton() {
  return html`<div class="dashboard-grid dashboard-grid-skeleton" aria-busy="true">
    ${[[0, 0, 3, 2], [0, 3, 3, 2], [0, 6, 3, 2], [0, 9, 3, 2], [2, 0, 6, 6], [2, 6, 6, 6]].map(([row, column, columnSpan, rowSpan], index) => html`<div
      key=${index}
      class="dashboard-grid-item"
      style=${{ gridColumn: `${column + 1} / span ${columnSpan}`, gridRow: `${row + 1} / span ${rowSpan}` }}
    ><div class="widget-card"><div class="widget-skeleton"><${Skeleton} width=${120} height=${14} /></div></div></div>`)}
  </div>`;
}

/**
 * @param {{ dashboardId: string | null }} props
 */
export function DashboardPage({ dashboardId }) {
  const isMobile = useIsMobile();
  const [loadError, setLoadError] = useState(/** @type {unknown} */ (null));
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
  const [dialog, setDialog] = useState(/** @type {DialogState} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const dashboards = dashboardsState.value;
  const dashboard = currentDashboardState.value?.id === dashboardId ? currentDashboardState.value : null;
  const isEditing = isEditingState.value;
  const canEdit = canEditDashboard(dashboard);

  useEffect(() => {
    document.title = dashboard ? `${dashboard.name} - ${t('dashboards.title')}` : t('dashboards.title');
  }, [dashboard?.name]);

  useEffect(() => {
    refreshDashboards().catch((error) => setLoadError(error));
    return () => {
      // Sair da página descarta o rascunho, como o "Cancelar".
      if (isEditingState.value) stopEditing();
      if (sidePanelState.value?.page === 'dashboard-widget') closeSidePanel();
    };
  }, []);

  useEffect(() => {
    if (dashboardId || !dashboards) return;
    if (dashboards.length > 0) navigate(`/dashboards/${dashboards[0].id}${window.location.search}`, { replace: true });
    else setIsLoadingDashboard(false);
  }, [dashboardId, dashboards]);

  useEffect(() => {
    if (!dashboardId) return undefined;
    let isCancelled = false;
    if (isEditingState.value) stopEditing();
    if (sidePanelState.value?.page === 'dashboard-widget') closeSidePanel();
    setIsLoadingDashboard(true);
    setLoadError(null);
    openDashboard(dashboardId)
      .then((opened) => {
        // id de um painel fundido no painel único (contrato 07, seção 12): a URL passa a ser a do painel aberto, sem
        // a aba do painel antigo e com os filtros de antes
        if (isCancelled || !opened || opened.id === dashboardId) return;
        navigate(redirectedDashboardPath(opened, window.location.search), { replace: true });
      })
      .catch((error) => {
        if (!isCancelled) setLoadError(error);
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingDashboard(false);
      });
    return () => {
      isCancelled = true;
    };
  }, [dashboardId]);

  const searchParams = searchParamsState.value;
  const tabParam = searchParams.get('tab');
  // volta de uma lista aberta por um clique num gráfico (contrato 07, seção 10): sem aba na URL, abre a do widget
  const widgetParam = searchParams.get(DASHBOARD_WIDGET_PARAM);
  const widgetTabId = !tabParam && widgetParam ? dashboard?.widgets?.find((widget) => widget.id === widgetParam)?.tabId ?? null : null;
  useEffect(() => {
    // fora da edição a aba aberta vem da URL (link compartilhável); em edição, das abas do rascunho
    if (!isEditingState.value) requestedTabKeyState.value = tabParam ?? widgetTabId;
  }, [tabParam, widgetTabId, dashboardId]);
  const filterState = readFilterState(searchParams, dashboard?.defaultFilters);
  const defaultState = readFilterState(new URLSearchParams(), dashboard?.defaultFilters);
  const filterKey = JSON.stringify(toRequestFilters(filterState));
  const requestFilters = useMemo(() => /** @type {import('../../data/dashboards.js').DashboardRequestFilters} */ (JSON.parse(filterKey)), [filterKey]);
  const isDefaultFilter = JSON.stringify(filterState) === JSON.stringify(defaultState);

  useEffect(() => {
    requestFiltersState.value = requestFilters;
  }, [requestFilters]);

  const changeFilters = (/** @type {Partial<DashboardFilterState>} */ changes) => {
    updateSearchParams(toSearchParamChanges({ ...filterState, ...changes }, dashboard?.defaultFilters));
  };
  const resetFilters = () => updateSearchParams(Object.fromEntries(FILTER_PARAM_KEYS.map((key) => [key, null])));
  const saveDefaultFilters = async () => {
    if (!dashboard) return;
    const defaultFilters = toDefaultFilters(filterState);
    try {
      await updateDashboard(dashboard.id, { defaultFilters });
      currentDashboardState.value = { ...dashboard, defaultFilters };
      resetFilters();
      showSuccessSnackbar(t('dashboards.messages.defaultFiltersSaved'));
    } catch (error) {
      showErrorSnackbar(error);
    }
  };

  const cancelEditing = () => {
    stopEditing();
    if (sidePanelState.value?.page === 'dashboard-widget') closeSidePanel();
  };
  const save = async () => {
    setIsSaving(true);
    try {
      const saved = await saveDraft();
      // a aba aberta (inclusive uma nova, que só agora tem id) vai para a URL
      const openTabId = requestedTabKeyState.value;
      updateSearchParams({ tab: openTabId && openTabId !== saved?.tabs?.[0]?.id ? openTabId : null });
      if (sidePanelState.value?.page === 'dashboard-widget') closeSidePanel();
      showSuccessSnackbar(t('dashboards.messages.saved'));
      refreshDashboards().catch(() => {});
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };

  const tabs = tabsState.value;
  const activeTabKey = activeTabKeyState.value;
  const items = isEditing
    ? activeTabDraftWidgetsState.value
    : widgetsInTab((dashboard?.widgets ?? []).map((widget, index) => ({ ...widget, key: widget.id ?? `widget-${index}` })), activeTabKey, tabs[0]?.key ?? null);
  // rola até o widget de onde veio o clique e deixa a URL só com a aba, para a troca de aba não rolar de novo
  const isGridReady = Boolean(dashboard && !isLoadingDashboard && dashboard.widgets?.some((widget) => widget.id === widgetParam));
  useEffect(() => {
    if (!widgetParam || !isGridReady || isEditing) return undefined;
    const frame = requestAnimationFrame(() => {
      document.querySelector(`[data-grid-key="${CSS.escape(widgetParam)}"]`)?.scrollIntoView({ block: 'center' });
      updateSearchParams({ [DASHBOARD_WIDGET_PARAM]: null, tab: tabParam ?? (widgetTabId && widgetTabId !== tabs[0]?.key ? widgetTabId : null) });
    });
    return () => cancelAnimationFrame(frame);
  }, [widgetParam, isGridReady]);
  const openTab = (/** @type {string} */ tabKey) => {
    selectTab(tabKey);
    if (!isEditing) updateSearchParams({ tab: tabKey === tabs[0]?.key ? null : tabKey });
  };

  const headerActions = isEditing
    ? html`
        <${Button} icon="IconPlus" title=${isMobile ? undefined : t('dashboards.edit.addWidget')} ariaLabel=${t('dashboards.edit.addWidget')} size="small" variant="secondary"
          onClick=${() => openDashboardWidgetPanel(null)} />
        <${Button} title=${t('common.cancel')} size="small" variant="secondary" onClick=${cancelEditing} />
        <${Button} title=${t('common.save')} icon="IconDeviceFloppy" size="small" accent="blue" isLoading=${isSaving} disabled=${!isDraftDirtyState.value} onClick=${save} />
      `
    : dashboard && canEdit
      ? html`<${Button} icon="IconPencil" title=${isMobile ? undefined : t('dashboards.edit.start')} ariaLabel=${t('dashboards.edit.start')} size="small" variant="secondary"
          onClick=${() => startEditing(dashboard)} />`
      : null;

  let body;
  if (loadError) {
    body = html`<${EmptyState} icon="IconAlertTriangle" title=${t('dashboards.loadError')} subtitle=${getErrorMessage(loadError)}
      action=${html`<${Button} title=${t('errors.reload')} variant="secondary" size="small" onClick=${() => window.location.reload()} />`} />`;
  } else if (dashboards && dashboards.length === 0 && !dashboardId) {
    body = html`<${EmptyState} icon="IconLayoutDashboard" title=${t('dashboards.emptyTitle')} subtitle=${t('dashboards.emptySubtitle')}
      action=${html`<${Button} icon="IconPlus" title=${t('dashboards.actions.create')} accent="blue" size="small" onClick=${() => setDialog({ kind: 'create' })} />`} />`;
  } else if (!dashboard || isLoadingDashboard) {
    body = html`<${DashboardSkeleton} />`;
  } else if (items.length === 0 && !isEditing) {
    body = html`<${EmptyState} icon="IconLayoutGridAdd" title=${t('dashboards.emptyWidgetsTitle')} subtitle=${canEdit ? t('dashboards.emptyWidgetsSubtitle') : undefined}
      action=${canEdit ? html`<${Button} icon="IconPlus" title=${t('dashboards.edit.addWidget')} accent="blue" size="small" onClick=${() => {
        startEditing(dashboard);
        openDashboardWidgetPanel(null);
      }} />` : null} />`;
  } else {
    body = html`<${DashboardGrid}
      items=${items}
      filters=${requestFilters}
      dashboardId=${dashboard.id}
      isEditing=${isEditing}
      selectedKey=${isEditing ? selectedWidgetKeyState.value : null}
      onLayoutChange=${setDraftLayout}
      onSelect=${(key) => {
        selectedWidgetKeyState.value = key;
        openDashboardWidgetPanel(key);
      }}
      onEdit=${(key) => {
        selectedWidgetKeyState.value = key;
        openDashboardWidgetPanel(key);
      }}
      onDuplicate=${(key) => {
        const copy = duplicateDraftWidget(key);
        if (copy) requestAnimationFrame(() => document.querySelector(`[data-grid-key="${CSS.escape(copy.key)}"]`)?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }));
      }}
      onRemove=${(key) => {
        removeDraftWidget(key);
        const page = sidePanelState.value;
        if (page?.page === 'dashboard-widget' && page.widgetKey === key) closeSidePanel();
      }}
      activeTabKey=${activeTabKey}
      moveTargets=${isEditing ? tabs.map((tab) => ({ key: tab.key, title: tab.title })) : []}
      onMoveToTab=${isEditing && tabs.length > 1 ? (key, tabKey) => {
        moveDraftWidgetToTab(key, tabKey);
        const title = tabs.find((tab) => tab.key === tabKey)?.title ?? '';
        showSuccessSnackbar(t('dashboardParity.tabs.widgetMoved', { title }));
      } : undefined}
    />`;
  }

  return html`<${PageCard}
    className="dashboard-page"
    header=${html`<${PageHeader}
      breadcrumb=${html`<${DashboardPicker} dashboard=${dashboard} canEdit=${canEdit} disabled=${isEditing}
        onAction=${(kind) => setDialog({ kind })} />`}
      actions=${headerActions}
    />`}
  >
    ${dashboard && !loadError
      ? html`<${DashboardFilterBar}
          state=${filterState}
          onChange=${changeFilters}
          isDefault=${isDefaultFilter}
          canSaveDefault=${canEdit && !isEditing}
          onSaveDefault=${saveDefaultFilters}
          onReset=${resetFilters}
        />`
      : null}
    ${dashboard && !loadError
      ? html`<${DashboardTabBar}
          tabs=${tabs}
          activeKey=${activeTabKey}
          isEditing=${isEditing}
          onSelect=${openTab}
          actions=${isEditing ? {
            onAdd: () => addDraftTab(),
            onRename: (key, title) => updateDraftTab(key, { title }),
            onMove: (key, direction) => moveDraftTab(key, direction),
            onDuplicate: (key) => duplicateDraftTab(key),
            onRemove: (key) => {
              if (removeDraftTab(key) && sidePanelState.value?.page === 'dashboard-widget') closeSidePanel();
            },
          } : undefined}
        />`
      : null}
    ${dashboard && !loadError && hasStageScope()
      ? html`<div class="dashboard-scope-banner" role="note" data-testid="dashboard-scope-banner">
          <${Icon} name="IconFilter" size=${14} />
          <span>${t('dashboardParity.scopedNumbers')}</span>
        </div>`
      : null}
    ${dashboard && !loadError && isEditing
      ? html`<div class="dashboard-edit-banner" role="status">
          <${Icon} name=${isMobile ? 'IconInfoCircle' : 'IconArrowsMove'} size=${14} />
          <span>${isMobile ? t('dashboards.edit.mobileHint') : t('dashboards.edit.hint')}</span>
        </div>`
      : null}
    <div class="dashboard-scroll">${body}</div>
    ${dialog
      ? html`<${DashboardDialog}
          dialog=${dialog}
          dashboard=${dashboard}
          onClose=${() => setDialog(null)}
          onDone=${async (nextId) => {
            setDialog(null);
            const list = await refreshDashboards().catch(() => dashboardsState.value ?? []);
            if (nextId && nextId !== dashboardId) navigate(`/dashboards/${nextId}`);
            else if (!nextId) navigate(list?.[0] ? `/dashboards/${list[0].id}` : '/dashboards', { replace: true });
          }}
        />`
      : null}
  <//>`;
}
